import { useEffect, useState } from 'react';

import useReducedMotion from '@/hooks/useReducedMotion';

/**
 * RankingHeroMock — la maqueta animada del hero: un mapa con el pin y, debajo,
 * el listado de Google en el que «Tu negocio» sube del cuarto puesto al primero
 * cada pocos segundos.
 *
 * Es la única pieza de la home con animación continua, y por eso lleva tres
 * cuidados que el diseño no traía:
 *
 * - **`prefers-reduced-motion` la para en seco.** No la ralentiza: la deja
 *   directamente en el estado final (primer puesto, con sus KPIs visibles), que
 *   es lo que la sección quiere contar. Una animación en bucle en el primer
 *   pliegue es justo el caso que esa preferencia existe para evitar.
 * - **No la lee un lector de pantalla.** Todo el bloque va `aria-hidden` y en su
 *   lugar hay una frase que dice lo que la animación enseña. Sin esto, el
 *   listado se anuncia entero cada vez que cambia de orden.
 * - **El intervalo se limpia al desmontar.** Obvio, pero es un `setInterval` en
 *   un entry point público: dejarlo vivo tras navegar a otra ruta del SPA es una
 *   fuga que nadie ve hasta que el móvil se calienta.
 *
 * Las filas se colocan en absoluto y se mueven con `translate3d` en vez de
 * reordenar el array: reordenar haría que React destruyera y recreara los nodos
 * y no habría transición ninguna, solo un salto.
 */
const ALTO_FILA = 64;

// Abajo (4.º) 2,2 s y arriba (1.º) 3,4 s: más tiempo en el estado que interesa.
const ESPERA_ABAJO = 2200;
const ESPERA_ARRIBA = 3400;
const CICLO = ESPERA_ABAJO + ESPERA_ARRIBA;

const RIVALES = [
  { n: 1, nota: '4,4', resenas: '312', estrellas: '★★★★☆' },
  { n: 2, nota: '4,2', resenas: '145', estrellas: '★★★★☆' },
  { n: 3, nota: '3,9', resenas: '86', estrellas: '★★★☆☆' },
];

export default function RankingHeroMock({ c }) {
  const quieto = useReducedMotion();
  // `puesto` es el índice de «Tu negocio» en la lista: 3 = cuarto, 0 = primero.
  const [puesto, setPuesto] = useState(quieto ? 0 : 3);

  useEffect(() => {
    if (quieto) {
      setPuesto(0);
      return undefined;
    }

    const t0 = Date.now();
    const id = setInterval(() => {
      const t = (Date.now() - t0) % CICLO;
      setPuesto(t < ESPERA_ABAJO ? 3 : 0);
    }, 120);

    return () => clearInterval(id);
  }, [quieto]);

  const primero = puesto === 0;

  // La lista con «Tu negocio» insertado en su puesto actual. El orden del array
  // NO cambia nunca; lo que cambia es la `y` de cada fila.
  const filas = [];
  RIVALES.forEach((r, i) => {
    filas.push({
      clave: `rival-${r.n}`,
      nombre: `${c.competidor} ${r.n}`,
      nota: r.nota,
      resenas: r.resenas,
      estrellas: r.estrellas,
      tu: false,
      y: (i < puesto ? i : i + 1) * ALTO_FILA,
    });
  });
  filas.splice(puesto, 0, {
    clave: 'tu',
    nombre: c.tuNegocio,
    nota: '4,9',
    resenas: '683',
    estrellas: '★★★★★',
    tu: true,
    y: puesto * ALTO_FILA,
  });

  return (
    <>
      <p className="sr-only">{c.rankingResumen}</p>

      <div
        aria-hidden="true"
        className="bg-white border border-v3-line rounded-3xl v3-shadow-lg overflow-hidden max-w-[420px] mx-auto"
      >
        <div className="relative h-[190px] bg-[#f2efe9] overflow-hidden">
          <CalleFalsa />

          {/* La caja de búsqueda */}
          <div className="absolute left-3.5 right-3.5 top-3.5 flex items-center gap-2.5 bg-white rounded-full px-3.5 py-2.5 shadow-[0_4px_14px_rgba(0,0,0,.12)]">
            <IconoMapa />
            <span className="text-[13.5px] font-medium flex-1 truncate">{c.busqueda}</span>
          </div>

          {/* El pin, con su posición actual */}
          <div
            className="absolute left-1/2 top-[52%] flex flex-col items-center gap-1.5 transition-transform [transition-duration:1100ms] [transition-timing-function:cubic-bezier(.2,1.25,.3,1)]"
            style={{ transform: `translate(-50%,-100%) scale(${primero ? 1.12 : 1})` }}
          >
            <span
              className="text-white rounded-full px-2.5 py-1.5 shadow-[0_4px_12px_rgba(0,0,0,.26)] text-[12.5px] font-bold transition-colors [transition-duration:1100ms]"
              style={{ background: primero ? '#16a34a' : '#dc2626' }}
            >
              #{puesto + 1}
            </span>
            <IconoPin />
          </div>

          {/* Los KPI solo aparecen cuando ya está el primero: son la consecuencia */}
          <div
            className="absolute right-3.5 bottom-3.5 flex flex-col gap-[7px] items-end transition-all [transition-duration:900ms] [transition-delay:260ms] [transition-timing-function:cubic-bezier(.16,.84,.2,1)]"
            style={{ opacity: primero ? 1 : 0, transform: `translateY(${primero ? 0 : 10}px)` }}
          >
            <span className="inline-flex items-center bg-v3-green text-white text-xs font-bold rounded-full px-2.5 py-1.5 shadow-[0_4px_12px_rgba(22,163,74,.34)]">
              {c.kpiLlamadas}
            </span>
            <span className="inline-flex items-center bg-[#2563eb] text-white text-xs font-bold rounded-full px-2.5 py-1.5 shadow-[0_4px_12px_rgba(37,99,235,.3)]">
              {c.kpiVisitas}
            </span>
          </div>
        </div>

        <div className="px-4.5 pt-3.5 pb-4.5" style={{ padding: '14px 18px 18px' }}>
          <div className="relative" style={{ height: filas.length * ALTO_FILA - 2 }}>
            {filas.map((f) => (
              <div
                key={f.clave}
                className="absolute left-0 right-0 top-0 transition-transform [transition-timing-function:cubic-bezier(.16,.84,.2,1)]"
                style={{
                  transform: `translate3d(0,${f.y}px,0)`,
                  zIndex: f.tu ? 6 : 1,
                  // La fila de «Tu negocio» se mueve despacio y las demás la
                  // siguen con retardo: se lee como que ella empuja y el resto
                  // se aparta, no como que la lista entera baraja.
                  transitionDuration: f.tu ? '1500ms' : '700ms',
                  transitionDelay: f.tu ? '0ms' : '1000ms',
                }}
              >
                <FilaRanking fila={f} primero={primero} c={c} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function FilaRanking({ fila, primero, c }) {
  const { tu } = fila;
  return (
    <div
      className="rounded-2xl px-3.5 py-3 flex items-center gap-3 transition-shadow duration-700"
      style={{
        background: tu ? '#ffffff' : '#fbfbfa',
        border: tu ? '2px solid #feae2c' : '1px solid rgba(0,0,0,.05)',
        boxShadow: tu
          ? `0 ${primero ? 10 : 6}px ${primero ? 28 : 20}px rgba(230,154,30,${primero ? '.34' : '.22'})`
          : 'none',
      }}
    >
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-bold tracking-[-.01em] truncate text-v3-ink">
          {fila.nombre}
        </span>
        <span className="flex items-center gap-1.5 mt-0.5">
          <span className="text-xs font-semibold text-v3-ink">{fila.nota}</span>
          <span className="text-[10.5px] text-v3-gold tracking-[.5px]">{fila.estrellas}</span>
          <span className="text-[11.5px] text-v3-muted">({fila.resenas})</span>
        </span>
      </span>

      {tu ? (
        <span
          className={`inline-flex items-center text-[11.5px] font-bold rounded-full px-2.5 py-1.5 whitespace-nowrap ${
            primero ? 'bg-v3-gold text-v3-ink' : 'bg-v3-greensoft text-v3-green'
          }`}
        >
          {primero ? c.mejorOpcion : c.subiendo}
        </span>
      ) : null}
    </div>
  );
}

/**
 * El callejero de mentira del fondo: unas bandas claras que se leen como calles
 * y manzanas. Son `div` de color y no una imagen porque pesan cero y escalan
 * sin pixelarse; la alternativa del diseño era una captura de 600 KB.
 */
function CalleFalsa() {
  return (
    <div className="absolute inset-0" aria-hidden="true">
      <div className="absolute left-0 right-0 top-[32%] h-3 bg-white" />
      <div className="absolute left-0 right-0 top-[72%] h-4 bg-[#fdf0d0] border-y border-[#f2e2b8]" />
      <div className="absolute top-0 bottom-0 left-[26%] w-[11px] bg-white" />
      <div className="absolute top-0 bottom-0 left-[64%] w-4 bg-[#fdf0d0] border-x border-[#f2e2b8]" />
      <div className="absolute left-[70%] top-0 w-[30%] h-[28%] bg-[#d8e8d0]" />
      <div className="absolute left-0 bottom-0 w-[20%] h-[24%] bg-[#cfe3ef]" />
      <div className="absolute left-[31%] top-[40%] w-[28%] h-[28%] bg-[#eae7e0]" />
    </div>
  );
}

/**
 * El icono de Google Maps de la caja de búsqueda.
 *
 * En el diseño esto era `uploads/Restaurantes perto1.jpg`: una FOTO de 500 px
 * pintada a 17×17. Aquí va como SVG — se ve nítido, pesa cero y no hay que
 * bajarse un fichero para que la home no salga rota.
 */
function IconoMapa() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" className="shrink-0" aria-hidden="true">
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7z" fill="#ea4335" />
      <circle cx="12" cy="9" r="2.6" fill="#fff" />
    </svg>
  );
}

function IconoPin() {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 24 24"
      className="block drop-shadow-[0_3px_6px_rgba(0,0,0,.3)]"
      aria-hidden="true"
    >
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7z" fill="#ea4335" />
      <circle cx="12" cy="9" r="2.6" fill="#fff" />
    </svg>
  );
}
